/**
 * Landing Product Purchase Component
 * Supports 2 separate products for color selection, dynamic bundle quantity tiers,
 * add-on strands, tree height/fullness line item properties, and gallery slider.
 */

if (!customElements.get('landing-product-purchase')) {
  class LandingProductPurchase extends HTMLElement {
    constructor() {
      super();

      // DOM Elements
      this.slides = this.querySelectorAll('.landing-product-purchase__slide');
      this.thumbBtns = this.querySelectorAll('.landing-product-purchase__thumb-btn');
      this.colorCards = this.querySelectorAll('.landing-product-purchase__color-card');
      this.extraCards = this.querySelectorAll('.landing-product-purchase__extra-card');
      this.bundleCards = this.querySelectorAll('.landing-product-purchase__bundle-card');
      this.heightSelect = this.querySelector('[data-spec="height"]');
      this.fullnessSelect = this.querySelector('[data-spec="fullness"]');
      this.specNote = this.querySelector('[data-spec-note]');
      this.specNoteWrap = this.querySelector('[data-spec-note-wrap]');
      this.addToCartBtn = this.querySelector('[data-action="add-to-cart"]');
      this.buyNowBtn = this.querySelector('[data-action="buy-now"]');
      this.priceCurrentEl = this.querySelector('[data-price-current]');
      this.priceCompareEl = this.querySelector('[data-price-compare]');
      this.bundlePrice1El = this.querySelector('[data-bundle-price-1]');
      this.bundlePrice2El = this.querySelector('[data-bundle-price-2]');
      this.bundleCompare2El = this.querySelector('[data-bundle-compare-2]');

      // State
      this.selectedColor = 'white'; // 'white' | 'multicolour'
      this.selectedBundle = 1; // Quantity: 1 or 2 (kept in sync with the Tree Height select in initTreeSpecs)
      this.minKits = 1; // Fewest kits allowed for the chosen tree height (2 for trees 8 ft and over)
      this.selectedExtras = new Set(); // Set of 'red', 'blue'

      // Parse JSON product config if provided
      const configScript = this.querySelector('[data-product-config]');
      this.config = configScript ? JSON.parse(configScript.textContent) : {};

      this.init();
    }

    connectedCallback() {
      this.initGallery();
      this.initColorSelection();
      this.initExtraStrands();
      this.initTreeSpecs();
      this.initBundleSelection();
      this.initActions();
    }

    init() {
      this.updatePriceDisplay();
    }

    /* ---------------- Gallery ---------------- */
    initGallery() {
      this.thumbBtns.forEach((btn, index) => {
        btn.addEventListener('click', () => {
          this.setActiveSlide(index);
        });
      });
    }

    setActiveSlide(index) {
      this.slides.forEach((slide, i) => {
        if (i === index) {
          slide.classList.add('is-active');
        } else {
          slide.classList.remove('is-active');
        }
      });

      this.thumbBtns.forEach((btn, i) => {
        if (i === index) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
        } else {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-selected', 'false');
        }
      });
    }

    /* ---------------- Color Selection (2 Separate Products) ---------------- */
    initColorSelection() {
      this.colorCards.forEach((card) => {
        card.addEventListener('click', () => {
          const colorKey = card.dataset.color;
          if (!colorKey) return;

          this.selectedColor = colorKey;

          this.colorCards.forEach((c) => {
            if (c.dataset.color === colorKey) {
              c.classList.add('is-active');
              c.setAttribute('aria-checked', 'true');
            } else {
              c.classList.remove('is-active');
              c.setAttribute('aria-checked', 'false');
            }
          });

          this.updatePriceDisplay();
        });
      });
    }

    /* ---------------- Extra Strands ---------------- */
    initExtraStrands() {
      this.extraCards.forEach((card) => {
        card.addEventListener('click', () => {
          const extraKey = card.dataset.extra;
          if (!extraKey) return;

          if (this.selectedExtras.has(extraKey)) {
            this.selectedExtras.delete(extraKey);
            card.classList.remove('is-selected');
            card.setAttribute('aria-checked', 'false');
          } else {
            this.selectedExtras.add(extraKey);
            card.classList.add('is-selected');
            card.setAttribute('aria-checked', 'true');
          }

          this.updatePriceDisplay();
        });
      });
    }

    /* ---------------- Tree Specs (Height & Fullness) ---------------- */
    initTreeSpecs() {
      // Kits needed by tree height: one kit fits trees up to 7 ft, trees 8 ft and over need two.
      // Keys are the <option> values of the Tree Height select; fullness does not change the count.
      const KITS_BY_HEIGHT = {
        'up-to-7ft': 1,
        '8ft+': 2,
      };

      const handleSpecsChange = () => {
        const height = this.heightSelect ? this.heightSelect.value : '';
        const kits = KITS_BY_HEIGHT[height];

        // Trees that need 2 kits: say so, and keep the 1 Kit bundle from being chosen
        this.minKits = kits === 2 ? 2 : 1;
        this.toggleKitsNote(this.minKits > 1);

        if (kits) this.setBundle(kits);
        else this.setBundle(this.selectedBundle);
      };

      if (this.heightSelect) {
        this.heightSelect.addEventListener('change', handleSpecsChange);
        // Start in sync with the preselected height (also covers browsers restoring form values on reload)
        handleSpecsChange();
      }
    }

    // Slides the "2 kits needed" note open/closed (the text is set on show so screen readers announce it)
    toggleKitsNote(show) {
      if (!this.specNote || !this.specNoteWrap) return;
      clearTimeout(this.kitsNoteTimer);

      if (show) {
        this.specNote.textContent = this.specNote.dataset.noteText || '';
        this.specNoteWrap.classList.add('is-visible');
      } else {
        this.specNoteWrap.classList.remove('is-visible');
        this.kitsNoteTimer = setTimeout(() => {
          this.specNote.textContent = '';
        }, 400);
      }
    }

    /* ---------------- Choose Your Bundle (Quantity: 1 or 2) ---------------- */
    initBundleSelection() {
      this.bundleCards.forEach((card) => {
        card.addEventListener('click', () => {
          const bundleQty = parseInt(card.dataset.bundleQty, 10) || 1;
          if (bundleQty < this.minKits) return;
          this.setBundle(bundleQty);
        });
      });
    }

    setBundle(bundleQty) {
      bundleQty = Math.max(bundleQty, this.minKits);
      this.selectedBundle = bundleQty; // 1 = 1 quantity, 2 = 2 quantity

      this.bundleCards.forEach((card) => {
        const qty = parseInt(card.dataset.bundleQty, 10) || 1;
        const unavailable = qty < this.minKits;
        card.classList.toggle('is-disabled', unavailable);
        card.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        card.tabIndex = unavailable ? -1 : 0;
        if (qty === bundleQty) {
          card.classList.add('is-active');
          card.setAttribute('aria-checked', 'true');
        } else {
          card.classList.remove('is-active');
          card.setAttribute('aria-checked', 'false');
        }
      });

      this.updatePriceDisplay();
    }

    /* ---------------- Money formatting ----------------
       Prices arrive in cents, already in the shopper's currency (Shopify converts them
       server-side; changing currency reloads the page). The shop's own format is read
       from a Liquid-formatted sample ("$1,234.56", "1.234,56 €", "¥1,235", ...), so the
       symbol position and separators always match the active currency. */
    getMoneyFormat() {
      if (this.moneyFormat) return this.moneyFormat;
      const sample = this.config.money_sample || '$1,234.56';
      const withDecimals = sample.match(/^(.*?)1([^\d]?)234([^\d])56(.*)$/s);
      if (withDecimals) {
        this.moneyFormat = {
          prefix: withDecimals[1],
          thousands: withDecimals[2],
          decimal: withDecimals[3],
          suffix: withDecimals[4],
          decimals: 2,
        };
      } else {
        // Zero-decimal currencies, e.g. JPY: "¥1,235"
        const noDecimals = sample.match(/^(.*?)1([^\d]?)23\d(.*)$/s) || [];
        this.moneyFormat = {
          prefix: noDecimals[1] || '$',
          thousands: noDecimals[2] || ',',
          decimal: '.',
          suffix: noDecimals[3] || '',
          decimals: 0,
        };
      }
      return this.moneyFormat;
    }

    /** Same output as Liquid's `money_without_trailing_zeros` + currency code, e.g. "$189 CAD". */
    formatMoney(cents) {
      const f = this.getMoneyFormat();
      const amount = Math.round(Number(cents) || 0);
      let whole;
      let fraction = '';

      if (f.decimals === 0) {
        whole = Math.round(amount / 100);
      } else {
        whole = Math.floor(amount / 100);
        const rest = amount % 100;
        if (rest !== 0) fraction = f.decimal + String(rest).padStart(2, '0');
      }

      const wholeStr = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, f.thousands);
      const currency = this.config.currency ? ` ${this.config.currency}` : '';
      return `${f.prefix}${wholeStr}${fraction}${f.suffix}${currency}`;
    }

    /* ---------------- Dynamic Pricing Calculations ---------------- */
    updatePriceDisplay() {
      const products = this.config.products || {};
      const product = products[this.selectedColor] || products.white;
      if (!product) return;

      const unit = product.unit_cents;
      const two = product.two_cents;
      const twoFull = product.two_full_cents;

      // Extra strands are added on top of whichever bundle is selected
      let extras = 0;
      this.selectedExtras.forEach((key) => {
        const extra = this.config.extras ? this.config.extras[key] : null;
        if (extra && extra.cents) extras += extra.cents;
      });

      const isTwo = this.selectedBundle === 2;
      const hasDiscount = twoFull > two;
      const total = (isTwo ? two : unit) + extras;
      const compare = isTwo && hasDiscount ? twoFull + extras : null;

      if (this.priceCurrentEl) this.priceCurrentEl.textContent = this.formatMoney(total);
      if (this.priceCompareEl) {
        this.priceCompareEl.hidden = !compare;
        this.priceCompareEl.textContent = compare ? this.formatMoney(compare) : '';
      }

      // Bundle cards show the selected colour's prices (without extras)
      if (this.bundlePrice1El) this.bundlePrice1El.textContent = this.formatMoney(unit);
      if (this.bundlePrice2El) this.bundlePrice2El.textContent = this.formatMoney(two);
      if (this.bundleCompare2El) {
        this.bundleCompare2El.hidden = !hasDiscount;
        this.bundleCompare2El.textContent = hasDiscount ? this.formatMoney(twoFull) : '';
      }
    }

    /* ---------------- Build Line Items with Properties ---------------- */
    getSelectedLineItems() {
      const items = [];
      const currentProduct = this.config.products ? this.config.products[this.selectedColor] : null;

      // Base product variant ID
      let mainVariantId = currentProduct ? currentProduct.variant_id : null;
      if (!mainVariantId && this.config.default_variant_id) {
        mainVariantId = this.config.default_variant_id;
      }

      // Build product line item properties for Tree Height and Tree Fullness
      const properties = {};

      const heightVal = this.heightSelect ? this.heightSelect.value : '';
      const fullnessVal = this.fullnessSelect ? this.fullnessSelect.value : '';

      if (heightVal && heightVal !== 'Select Height') {
        const heightOption = this.heightSelect.selectedOptions[0];
        properties['Tree Height'] = heightOption ? heightOption.textContent.trim() : heightVal;
      }
      if (fullnessVal) {
        properties['Tree Fullness'] = fullnessVal;
      }

      if (this.selectedColor) {
        properties['Color'] = this.selectedColor === 'white' ? 'White' : 'Multicolour';
      }

      if (mainVariantId) {
        items.push({
          id: mainVariantId,
          quantity: this.selectedBundle, // Quantity 1 (1 Kit) or Quantity 2 (2 Kits)
          properties: Object.keys(properties).length > 0 ? properties : undefined
        });
      }

      // Extra strands add-on items
      this.selectedExtras.forEach((extraKey) => {
        const extraProd = this.config.extras ? this.config.extras[extraKey] : null;
        if (extraProd && extraProd.variant_id) {
          items.push({
            id: extraProd.variant_id,
            quantity: 1,
            properties: {
              'Add-on For': `${this.selectedColor === 'white' ? 'White' : 'Multicolour'} RingTree Kit`
            }
          });
        }
      });

      return items;
    }

    /* ---------------- Actions (Add To Cart & Buy Now) ---------------- */
    initActions() {
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleAddToCart();
        });
      }

      if (this.buyNowBtn) {
        this.buyNowBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleBuyNow();
        });
      }
    }

    async handleAddToCart() {
      const items = this.getSelectedLineItems();
      if (!items.length) {
        console.warn('No variant ID found. Please assign products in the theme editor.');
        alert('Please select or assign products in the Theme Editor to add to cart.');
        return;
      }

      this.addToCartBtn.classList.add('is-loading');

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: items,
            sections: 'cart-drawer,cart-icon-bubble'
          })
        });

        const data = await response.json();

        if (response.ok) {
          // Trigger cart drawer if present in theme
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            // Update cart drawer section HTML
            if (data.sections && data.sections['cart-drawer']) {
              const drawerInner = cartDrawer.querySelector('#CartDrawer') || cartDrawer;
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = data.sections['cart-drawer'];
              const newDrawerContent = tempDiv.querySelector('#CartDrawer');
              if (newDrawerContent && drawerInner) {
                drawerInner.innerHTML = newDrawerContent.innerHTML;
              }
            }

            // Update cart icon bubble count
            if (data.sections && data.sections['cart-icon-bubble']) {
              const cartIconBubble = document.getElementById('cart-icon-bubble');
              if (cartIconBubble) {
                cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
              }
            }

            // Publish pubsub cartUpdate event
            if (window.publish && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'landing-product-purchase',
                cartData: data
              });
            }

            // Open drawer
            cartDrawer.classList.remove('is-empty');
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              cartDrawer.classList.add('active', 'animate');
            }
          } else {
            // Fallback: direct to cart page
            window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
          }
        } else {
          console.error('Cart add error:', data.description || data.message);
          alert(data.description || 'Could not add to cart. Please try again.');
        }
      } catch (err) {
        console.error('Add to cart exception:', err);
      } finally {
        this.addToCartBtn.classList.remove('is-loading');
      }
    }

    async handleBuyNow() {
      const items = this.getSelectedLineItems();
      if (!items.length) {
        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
        return;
      }

      this.buyNowBtn.classList.add('is-loading');

      try {
        // Add items with properties to cart first, then redirect to checkout
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items: items })
        });

        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
      } catch (err) {
        console.error('Buy Now error:', err);
        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
      } finally {
        this.buyNowBtn.classList.remove('is-loading');
      }
    }
  }

  customElements.define('landing-product-purchase', LandingProductPurchase);
}
